import { defineComponent, Fragment, PropType, computed, CSSProperties, ref, onMounted, onUnmounted, watch } from 'vue';
import './Table.sass';
import TableSkeleton from './TableSkeleton.vue';

export const TABLE_HEADER_VARIANTS = {
  default: 'default',
  spec: 'spec',
} as const;

export type TableHeaderVariant = (typeof TABLE_HEADER_VARIANTS)[keyof typeof TABLE_HEADER_VARIANTS];

export const TABLE_HEADER_ALIGNMENTS = {
  left: 'left',
  right: 'right',
} as const;

export type TableHeaderAlign = (typeof TABLE_HEADER_ALIGNMENTS)[keyof typeof TABLE_HEADER_ALIGNMENTS];

export type TableHeader = {
  label: string;
  value: string;
  style?: object;
  sortable?: boolean;
  align?: TableHeaderAlign;
};

enum SortGlyphState {
  Idle = 'idle',
  Ascending = 'ascending',
  Descending = 'descending',
}

const DEFAULT_SORT_GLYPHS: Record<SortGlyphState, string> = {
  [SortGlyphState.Idle]: '',
  [SortGlyphState.Ascending]: '↑',
  [SortGlyphState.Descending]: '↓',
};

const SPEC_SORT_GLYPHS: Record<SortGlyphState, string> = { ...DEFAULT_SORT_GLYPHS, [SortGlyphState.Idle]: '↕' };

const ARIA_SORT: Record<SortGlyphState, 'none' | 'ascending' | 'descending'> = {
  [SortGlyphState.Idle]: 'none',
  [SortGlyphState.Ascending]: 'ascending',
  [SortGlyphState.Descending]: 'descending',
};

const PINNED_CELL_Z_INDEX = 20;

const BOUNDED_SCROLLER_STYLE = 'max-height: calc(100vh - 300px); z-index: 0;';
const UNBOUNDED_SCROLLER_STYLE = 'z-index: 0;';

export default defineComponent({
  name: 'TableRenderer',
  props: {
    headers: {
      type: Array as PropType<TableHeader[]>,
      required: true,
    },
    items: {
      type: Array as PropType<unknown[]>,
      required: true,
    },
    contained: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    layoutFixed: {
      type: Boolean,
      default: false,
    },
    sortConfig: {
      type: Object as PropType<{ field: { key: string; label: string }; desc: boolean }>,
      default: null,
    },
    // Only the spec variant honours header.align because some existing consumers already pass it.
    headerVariant: {
      type: String as PropType<TableHeaderVariant>,
      default: TABLE_HEADER_VARIANTS.default,
    },
    pinnedColumns: {
      type: Number,
      default: 0,
      validator: (value: number) => Number.isInteger(value) && value >= 0,
    },
    // Fits the table to its container with wrapping headers, and body cells stay nowrap unless given whitespace-normal.
    fitWidth: {
      type: Boolean,
      default: false,
    },
    // Drops the max height so the page scrolls instead of the table, so the sticky header no longer sticks.
    unbounded: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['sort'],
  setup(props, ctx) {
    const tableContainer = ref<HTMLElement | null>(null);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator?.userAgent);
    const showRightIndicator = ref(false);

    const updateScrollIndicators = () => {
      if (!tableContainer.value) return;
      const { scrollLeft, scrollWidth, clientWidth } = tableContainer.value;
      showRightIndicator.value = scrollLeft + clientWidth < scrollWidth - 1;
    };

    let scrollerResizeObserver: ResizeObserver | undefined;

    // A scroller that mounts hidden (v-show) measures zero, so re-measure the fade once it gets a size.
    const observeScroller = (container: HTMLElement | null) => {
      scrollerResizeObserver?.disconnect();
      if (!container || typeof ResizeObserver === 'undefined') return;
      scrollerResizeObserver = new ResizeObserver(updateScrollIndicators);
      scrollerResizeObserver.observe(container);
    };

    // The container only renders once loading ends, so wire the fade whenever it appears.
    watch(
      tableContainer,
      (container, previous) => {
        previous?.removeEventListener('scroll', updateScrollIndicators);
        container?.addEventListener('scroll', updateScrollIndicators);
        observeScroller(container);
        updateScrollIndicators();
      },
      { flush: 'post' },
    );
    onMounted(() => window.addEventListener('resize', updateScrollIndicators));
    onUnmounted(() => {
      tableContainer.value?.removeEventListener('scroll', updateScrollIndicators);
      window.removeEventListener('resize', updateScrollIndicators);
      scrollerResizeObserver?.disconnect();
    });

    const headerRow = ref<HTMLTableRowElement | null>(null);
    const pinnedOffsets = ref<number[]>([]);
    let pinnedResizeObserver: ResizeObserver | undefined;

    const pinnedHeaderCells = () => Array.from(headerRow.value?.children ?? []).slice(0, props.pinnedColumns);

    const measurePinnedOffsets = () => {
      const widths = pinnedHeaderCells().map(cell => cell.getBoundingClientRect().width);
      pinnedOffsets.value = widths.map((_, index) => widths.slice(0, index).reduce((sum, width) => sum + width, 0));
    };

    const observePinnedColumns = () => {
      pinnedResizeObserver?.disconnect();
      if (props.pinnedColumns <= 0 || !headerRow.value) return;
      measurePinnedOffsets();
      if (typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(measurePinnedOffsets);
      pinnedHeaderCells().forEach(cell => observer.observe(cell));
      pinnedResizeObserver = observer;
    };

    // Keyed on the pinned headers' values, so a parent rebuilding an identical headers array doesn't re-observe.
    const pinnedHeaderKey = () =>
      props.headers
        .slice(0, props.pinnedColumns)
        .map(header => header.value)
        .join('\u0000');

    watch([headerRow, pinnedHeaderKey, () => props.pinnedColumns], observePinnedColumns, { flush: 'post' });
    onUnmounted(() => pinnedResizeObserver?.disconnect());

    const pinnedStyle = (index: number): CSSProperties | undefined => {
      if (index >= props.pinnedColumns) return undefined;
      return { position: 'sticky', left: `${pinnedOffsets.value[index] ?? 0}px`, zIndex: PINNED_CELL_Z_INDEX };
    };

    const headerStyle = (header: TableHeader, index: number) => {
      const pinned = pinnedStyle(index);
      if (!pinned) return header.style as CSSProperties;
      return { ...header.style, ...pinned };
    };

    const classes = computed(() => [
      props.contained ? 'rounded-xl border border-solid border-neutral-200' : '',
      isSafari ? 'overflow-auto relative' : 'overflow-auto table-container-scroller relative',
    ]);

    const isSpecHeader = computed(() => props.headerVariant === TABLE_HEADER_VARIANTS.spec);

    const onHeaderClick = (header: TableHeader) => {
      if (header.sortable) ctx.emit('sort', header.value);
    };

    const sortGlyphState = (header: TableHeader) => {
      if (!props.sortConfig || props.sortConfig.field.key !== header.value) return SortGlyphState.Idle;
      return props.sortConfig.desc ? SortGlyphState.Descending : SortGlyphState.Ascending;
    };

    const renderSpecHeader = (header: TableHeader, index: number) => {
      const glyphState = sortGlyphState(header);
      return (
        <th
          key={header.value}
          class={{
            sortable: header.sortable,
            'sort-active': header.sortable && glyphState !== SortGlyphState.Idle,
            'align-right': header.align === TABLE_HEADER_ALIGNMENTS.right,
            pinned: index < props.pinnedColumns,
          }}
          style={headerStyle(header, index)}
          aria-sort={header.sortable ? ARIA_SORT[glyphState] : undefined}
          onClick={() => onHeaderClick(header)}
        >
          <div class="spec-header-cell">
            <span>{header.label}</span>
            {header.sortable && (
              <span class={['sort-glyph', `sort-glyph--${glyphState}`]}>{SPEC_SORT_GLYPHS[glyphState]}</span>
            )}
          </div>
        </th>
      );
    };

    const renderDefaultHeader = (header: TableHeader, index: number) => (
      <th
        key={header.value}
        class={[
          'font-normal py-2 px-4 text-left',
          header.sortable ? 'cursor-pointer' : '',
          { pinned: index < props.pinnedColumns },
        ]}
        style={headerStyle(header, index)}
        onClick={() => onHeaderClick(header)}
      >
        <div class={header.sortable ? 'flex items-center' : ''}>
          <span>{header.label}</span>
          {header.sortable && <span class="ml-2">{DEFAULT_SORT_GLYPHS[sortGlyphState(header)]}</span>}
        </div>
      </th>
    );

    const renderHeader = (header: TableHeader, index: number) => {
      if (ctx.slots.header) return ctx.slots.header({ header, index, pinnedStyle });
      if (isSpecHeader.value) return renderSpecHeader(header, index);
      return renderDefaultHeader(header, index);
    };

    return () => (
      <div class="relative">
        {props.loading && <TableSkeleton headers={props.headers} fitWidth={props.fitWidth} />}
        {!props.loading && (
          <Fragment>
            {showRightIndicator.value && <div class="table-scroll-indicator right" />}
            <div
              ref={tableContainer}
              class={classes.value}
              style={props.unbounded ? UNBOUNDED_SCROLLER_STYLE : BOUNDED_SCROLLER_STYLE}
            >
              <table
                class={['w-full orderprotection', { 'spec-header': isSpecHeader.value, 'fit-width': props.fitWidth }]}
                style={{
                  tableLayout: props.layoutFixed ? 'fixed' : 'auto',
                  minWidth: props.fitWidth ? undefined : 'max-content',
                }}
              >
                <thead class="text-xsm bg-neutral-100 sticky top-0 z-40">
                  <tr ref={headerRow} class="relative">
                    {props.headers.map(renderHeader)}
                  </tr>
                </thead>
                <tbody>
                  {props.items.map(item => (ctx.slots.item ? ctx.slots.item({ item, pinnedStyle }) : null))}
                </tbody>
                {ctx.slots.tfoot && <tfoot>{ctx.slots.tfoot()}</tfoot>}
              </table>
            </div>
          </Fragment>
        )}
        {ctx.slots.footer && <div class="flex w-full justify-end">{ctx.slots.footer()}</div>}
      </div>
    );
  },
});
